/**
 * Flashcard Viewer - Hiển thị và tương tác với flashcards
 * Flip animation và study mode
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, RotateCw, Check, X, BookOpen, Palette } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import ProfessionalFlashcard from './ProfessionalFlashcard'
import type { Flashcard } from '../../types/flashcard'

interface FlashcardViewerProps {
  flashcards: Flashcard[]
  onReview?: (flashcardId: string, correct: boolean) => void
  onClose?: () => void
}

const FlashcardViewer: React.FC<FlashcardViewerProps> = ({
  flashcards,
  onReview,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [reviewedCards, setReviewedCards] = useState<Set<number>>(new Set())
  const [designVariant, setDesignVariant] = useState<1 | 2 | 3>(1)
  const [soundEnabled, setSoundEnabled] = useState(true)

  const currentCard = flashcards[currentIndex]
  const progress = ((currentIndex + 1) / flashcards.length) * 100

  // Sound function
  const playSound = (type: 'navigate' | 'flip' | 'success' | 'error') => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      const createTone = (frequency: number, duration: number, type: OscillatorType = 'sine') => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.type = type
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)
        
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + duration)
      }
      
      switch (type) {
        case 'navigate':
          createTone(330, 0.1) // E4 note
          setTimeout(() => createTone(440, 0.1), 50) // A4 note
          break
        case 'flip':
          createTone(440, 0.1) // A4 note
          setTimeout(() => createTone(554, 0.1), 50) // C#5 note
          break
        case 'success':
          createTone(523, 0.2) // C5 note
          setTimeout(() => createTone(659, 0.2), 100) // E5 note
          setTimeout(() => createTone(784, 0.3), 200) // G5 note
          break
        case 'error':
          createTone(200, 0.3, 'sawtooth') // Low frequency
          break
      }
    } catch (error) {
      console.log('Audio not supported')
    }
  }

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          prevCard()
          break
        case 'ArrowRight':
          event.preventDefault()
          nextCard()
          break
        case 'ArrowUp':
        case 'ArrowDown':
        case ' ':
          event.preventDefault()
          flipCard()
          break
        case '1':
          event.preventDefault()
          handleReview(false)
          break
        case '2':
          event.preventDefault()
          handleReview(true)
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentIndex, isFlipped, soundEnabled])

  // Navigate to next card
  const nextCard = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setIsFlipped(false)
      // Auto-rotate design variant
      setDesignVariant((prev) => (prev % 3) + 1 as 1 | 2 | 3)
      // Play navigation sound
      if (soundEnabled) {
        playSound('navigate')
      }
    }
  }

  // Navigate to previous card
  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
      setIsFlipped(false)
      // Auto-rotate design variant
      setDesignVariant((prev) => (prev % 3) + 1 as 1 | 2 | 3)
      // Play navigation sound
      if (soundEnabled) {
        playSound('navigate')
      }
    }
  }

  // Flip card
  const flipCard = () => {
    setIsFlipped(!isFlipped)
    // Play flip sound
    if (soundEnabled) {
      playSound('flip')
    }
  }

  // Change design variant
  const changeDesign = () => {
    setDesignVariant((prev) => (prev % 3) + 1 as 1 | 2 | 3)
  }

  // Handle review (correct/incorrect)
  const handleReview = (correct: boolean) => {
    if (onReview && currentCard) {
      onReview(currentCard.id, correct)
    }
    setReviewedCards(new Set([...reviewedCards, currentIndex]))
    
    // Auto advance to next card
    setTimeout(() => {
      nextCard()
    }, 500)
  }

  // Get difficulty color
  const getDifficultyColor = (difficulty: 'easy' | 'medium' | 'hard'): string => {
    switch (difficulty) {
      case 'easy': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'hard': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  if (!currentCard) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-xl">
        <BookOpen className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-xl text-gray-600">Không có flashcard nào</p>
      </div>
    )
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-gray-600">
              Flashcard {currentIndex + 1} / {flashcards.length}
            </p>
            <h3 className="text-lg font-semibold text-gray-800">
              {currentCard.documentName}
            </h3>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getDifficultyColor(currentCard.difficulty)}`}></div>
              <span className="text-sm text-gray-600">
                {currentCard.difficulty === 'easy' ? 'Dễ' : currentCard.difficulty === 'medium' ? 'Trung bình' : 'Khó'}
              </span>
            </div>
            
            {/* Design Variant Selector */}
            <button
              onClick={changeDesign}
              className="flex items-center space-x-2 px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Thay đổi thiết kế"
            >
              <Palette className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-600">Mẫu {designVariant}</span>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Professional Flashcard */}
      <div className="relative w-[700px] h-[500px] mx-auto mb-6 mt-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentIndex}-${designVariant}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
          >
            <ProfessionalFlashcard
              flashcard={currentCard}
              isFlipped={isFlipped}
              onFlip={flipCard}
              designVariant={designVariant}
              enableSound={soundEnabled}
              onSoundToggle={setSoundEnabled}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={prevCard}
          disabled={currentIndex === 0}
          className="px-8 py-4 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 transition-colors text-lg"
        >
          <ChevronLeft className="w-6 h-6" />
          <span className="font-semibold">Trước</span>
        </button>

        <div className="flex items-center space-x-6">
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-700">
              {reviewedCards.size} / {flashcards.length} đã xem
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-green-600">
              {Math.round((reviewedCards.size / flashcards.length) * 100)}%
            </p>
            <p className="text-sm text-gray-600">Hoàn thành</p>
          </div>
          
          {/* Review Buttons (only show when flipped) - fixed width to maintain layout */}
          <div className="w-48 flex justify-center">
            {isFlipped && onReview && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex space-x-3"
              >
                <button
                  onClick={() => handleReview(false)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold flex items-center space-x-2 transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  <span>Chưa nhớ</span>
                </button>
                <button
                  onClick={() => handleReview(true)}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold flex items-center space-x-2 transition-colors text-sm"
                >
                  <Check className="w-4 h-4" />
                  <span>Đã nhớ</span>
                </button>
              </motion.div>
            )}
          </div>
        </div>

        <button
          onClick={nextCard}
          disabled={currentIndex === flashcards.length - 1}
          className="px-8 py-4 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 transition-colors text-lg"
        >
          <span className="font-semibold">Tiếp</span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Keyboard Shortcuts Guide */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-blue-700 font-medium mb-2">Phím tắt:</p>
          <div className="flex justify-center space-x-6 text-xs text-blue-600">
            <span>← → Chuyển thẻ</span>
            <span>↑ ↓ Space Lật thẻ</span>
            <span>1 Chưa nhớ</span>
            <span>2 Đã nhớ</span>
          </div>
        </div>
      </div>


      {/* Close Button */}
      {onClose && (
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 underline"
          >
            Quay lại
          </button>
        </div>
      )}
    </div>
  )
}

export default FlashcardViewer

